import type { Participant, StreamType, User } from '../../types'
import { Message, RoomStateItemX, TipMessage } from '../../types/mfc'
import { loadUserIfNeeded } from '../actions/loadUserIfNeeded'
import { goToRoom } from '../actions/nav'
import { streamPublicShow } from '../actions/room'
import { roomAction } from '../actions/workerActions'
import { HEALTH, RoomAction } from '../constants/enums'
import { isLounge } from '../mfcLogic/userProperties/user'
import { isFloatingRoomPm } from '../state/modals/state.floater'
import { USERSSTATE, getUser, idToSlug, slugToID } from '../state/state.users'
import { isTouchDevice } from '../utilities/browser'
import { safeObj } from '../utilities/safe'
import { pubSub } from './pubSub'
import { appState } from './state.app'

const setRoomState = appState('ROOM')
export const getRoomState = () => appState().ROOM

export const updateRoomState = (
    id: number,
    roomStateUpdate: Partial<RoomStateItemX>,
    immediately = false,
) => {
    setRoomState({
        modifications: {
            [id]: roomStateUpdate,
        },
        immediately,
    })
}

export const isModelViewable = (id: number) => {
    const model = getUser(id)
    const inLounge = isLounge(id)
    const loungeSurrogateID = inLounge
        ? getRoomState()[id]?.loungeSurrogateID
        : null
    const {
        isBroadcasting,
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isKicked,
        isOnline,
        banned,
    } = safeObj(model)
    const isPM = isFloatingRoomPm(id)
    // if its a pm and we're not on mobile, then we have quick reply spawn rules
    if (isPM && !isTouchDevice) {
        if (isOnline) return true
        return false
    }
    // be generous with lounge check
    if (inLounge && loungeSurrogateID) return true
    if (isTruePrivate || isPrivateShow || isGroupShow || isClubShow) return true

    if (!isBroadcasting) return false
    if (isKicked || banned) return false
    return true
}
export const subscribeRoom = (
    id: number,
    options: { streamType?: StreamType; forceVoyeur?: boolean } = {},
) => {
    const { streamType, forceVoyeur } = options
    // console.trace(
    //     `[app-state: room ${id}] SUBSCRIBE ROOM. Stream Type:`,
    //     streamType,
    // )
    roomAction(RoomAction.SUBSCRIBE, { id, streamType, forceVoyeur })
}

export const swapRoom = (
    id: number,
    options: {
        unsubscribeStreamType?: StreamType
        subscribeStreamType?: StreamType
    } = {},
) => {
    const { unsubscribeStreamType, subscribeStreamType } = options
    // console.log(
    //     `[app-state: room ${id}] SWAPPING ROOM. Subscribe: ${subscribeStreamType}, Unsubscribe: ${unsubscribeStreamType}`,
    // )

    roomAction(RoomAction.SWAP, {
        unsubscribeStreamType,
        subscribeStreamType,
        id,
    })
}

export const unsubscribeRoom = (
    id: number,
    options: { streamType?: StreamType } = {},
) => {
    const { streamType } = options
    // console.log(
    //     `[app-state: room ${id}] UNSUBSCRIBE ROOM. Stream Type:`,
    //     streamType,
    // )
    roomAction(RoomAction.UNSUBSCRIBE, { id, streamType })
}

pubSub.USERS.on(
    'UPDATE:subscribed',
    ({ current: isSubscribed, id }: { current: boolean; id: number }) => {
        const { [id]: existingRoomState } = getRoomState()

        if (!existingRoomState) {
            if (isSubscribed) {
                unsubscribeRoom(id)
            }

            return
        }

        // console.log(
        //     `[app-state: room ${id}] "isSubscribed" changed. ` +
        //         `Current: ${isSubscribed}`, // Previous: ${existingIsSubscribed}`,
        // )

        setRoomState({
            modifications: {
                [id]: {
                    isSubscribed,
                },
            },
            immediately: true,
        })
    },
)

pubSub.HEALTH.on('HEALTH', (status) => {
    if (status.current === HEALTH.DISCONNECTED) {
        return
    }
    Object.keys(getRoomState()).forEach((id) => {
        setRoomMessagesStaleState(true, Number(id))
        // clearRoomMessagesState(Number(id))
    })
})

pubSub.USERS.on(
    'UPDATE:streamType',
    ({
        previous: previousStreamType,
        current: streamType,
        id,
    }: {
        previous: StreamType
        current: StreamType
        id: number
    }) => {
        // console.log(
        //     `[app-state: room ${id}] "streamType" changed. ` +
        //         `Current: ${streamType}, Previous: ${previousStreamType}`,
        // )

        const { [id]: existingRoomState } = getRoomState()

        if (!existingRoomState && streamType !== 'public') {
            //
            //
            // Marc: Addressing race conditions betweeen this function and the
            // setRoomIDState when initiating a voyeur, private, or club show
            // from a PM.
            //
            //
            setRoomIDState(id)
        }

        if (!existingRoomState?.isOpen && streamType !== 'public') {
            goToRoom(idToSlug[id])
        }

        if (streamType && !previousStreamType) {
            //
            // Marc:
            // Scenario
            // User SSR into room or came in fresh into the room from somewhere
            //
            subscribeRoom(id, { streamType })
            return
        }

        if (streamType === 'voyeur' && previousStreamType === 'public') {
            setRoomIDState(id)

            //
            // Marc:
            // Scenario
            // User wants to go into a "voyeur" show, in this case, we are
            // already subscribed to public and there's nothing we need to do
            //
            removeEndOfShowMessage(id)
            return
        }

        if (streamType) {
            clearRoomMessagesState(id)
        }

        if (previousStreamType && streamType) {
            swapRoom(id, {
                unsubscribeStreamType: previousStreamType,
                subscribeStreamType: streamType,
            })

            if (streamType !== 'public' || previousStreamType === 'public') {
                return
            }

            resolveShowExitMessages(id, previousStreamType)
        }
    },
)

pubSub.USERS.on(
    'UPDATE:isClubShow',
    ({ current: isClubShow, id }: { current: boolean; id: number }) => {
        const { [id]: existingRoomState } = getRoomState()

        if (!existingRoomState) {
            return
        }

        resolveClubShowMessage(isClubShow, id)
    },
)

const resolveShowExitMessages = (
    id: number,
    previousStreamType: StreamType,
) => {
    const showExitMessage = {
        text: `You have exited the ${previousStreamType} show.`,
        timestamp: Date.now(),
        endShow: true,
        system: true,
    }

    setRoomMessagesState(showExitMessage, id)
}

const removeExistingExtraContent = (
    messages: Array<Message>,
    options: { topicUpdate?: boolean } = {},
) => {
    const { topicUpdate } = options

    if (!messages || !topicUpdate) {
        return messages
    }

    return messages.filter((message) => !message.extraContent)
}

const removeExistingClubShowMessage = (id: number) => {
    const { [id]: existingRoom } = getRoomState()

    if (!existingRoom) {
        return
    }

    const { messages: existingMessages = [] } = existingRoom

    const filteredMessages = existingMessages.filter(
        (message) => !message.clubShow,
    )

    if (filteredMessages.length === existingMessages.length) {
        return
    }

    setRoomState({
        modifications: {
            [id]: {
                messages: filteredMessages,
            },
        },
    })
}

const removeEndOfShowMessage = (id: number) => {
    const { [id]: existingRoom } = getRoomState()

    if (!existingRoom) {
        return
    }

    const { messages: existingMessages } = existingRoom

    if (!existingMessages) {
        return
    }

    const filteredMessages = existingMessages.filter(
        (message) => !message.endShow,
    )

    if (filteredMessages.length === existingMessages.length) {
        return
    }

    setRoomState({
        modifications: {
            [id]: {
                messages: filteredMessages,
            },
        },
    })
}

const reorderEndMessage = (messages: Array<Message>) => {
    const exitMessage = messages.find((message) => message.endShow)

    if (!exitMessage) {
        return null
    }

    const filteredMessages = messages.filter((message) => !message.endShow)
    const welcomMessageIndex = filteredMessages.findIndex(
        (message) => message.welcome,
    )

    if (welcomMessageIndex === -1) {
        return null
    }

    return [
        ...filteredMessages.slice(0, welcomMessageIndex),
        exitMessage,
        filteredMessages[welcomMessageIndex],
        ...filteredMessages.slice(welcomMessageIndex + 1),
    ]
}

const reorderMessage = (
    messages: Array<Message>,
    filter: (value: Message) => unknown,
    skipLast = false,
) => {
    const messageIndex = messages.findIndex(filter)

    if (messageIndex === -1) {
        return null
    }

    const existingMessage = messages[messageIndex]
    const filteredMessages = messages.filter((message) => !filter(message))

    if (skipLast) {
        return [...filteredMessages, existingMessage]
    }

    return [
        ...filteredMessages.slice(0, -1),
        existingMessage,
        filteredMessages[filteredMessages.length - 1],
    ]
}

const insertLoungeModelPreviews = async (
    text: string,
): Promise<[string, { slug: string; id: number }[]]> => {
    const words = text.split(' ')

    const modelPreviews = []
    const wordsPromises = words.map(async (word) => {
        if (!word) return word
        const possibleSlug = word.toLowerCase()
        const id = slugToID[possibleSlug]
        if (!id || word.length < 5) {
            return word
        }

        const user = await loadUserIfNeeded(id)

        if (!user) {
            return word
        }

        const { isLounge, isModel, slug } = user

        if (isModel && !isLounge) {
            if (!modelPreviews.includes(id)) {
                modelPreviews.push({ id, slug })
            }
            return `{{model|${id}}}`
        }

        return word
    })

    const resolvedWords = await Promise.all(wordsPromises)

    return [resolvedWords.join(' '), modelPreviews]
}

const processLoungeMessage = async (
    message: Message,
    isLounge: boolean,
): Promise<[Message, { slug: string; id: number }[]]> => {
    if (!isLounge || message.tip) {
        return [message, []]
    }

    const { text } = message
    const [processedText, modelPreviews] = await insertLoungeModelPreviews(text)
    return [{ ...message, text: processedText }, modelPreviews]
}

const generateExtraContentText = (model: User) => {
    const {
        shareRecordings,
        name: modelName,
        hasWhiteboard,
        shareAlbums,
    } = model

    const hasShareContent = shareAlbums > 0 || shareRecordings > 0
    const hasRecordings = shareRecordings > 0
    const hasAlbums = shareAlbums > 0
    const whiteboardMsg = hasWhiteboard
        ? '{{whiteboard}}' + (hasShareContent ? ' ' : '')
        : ''
    const albumsMsg = `{{MFCShareAlbums|https://share.myfreecams.com/${modelName}/albums|${shareAlbums}}}`
    const recordingsMsg = `{{MFCShareRecordings|https://share.myfreecams.com/${modelName}/recordings|${shareRecordings}}}`

    if (!hasShareContent) {
        return whiteboardMsg
    }

    if (hasAlbums && !hasRecordings) {
        return `${whiteboardMsg}${albumsMsg}`
    }

    if (!hasAlbums && hasRecordings) {
        return `${whiteboardMsg}${recordingsMsg}`
    }

    if (hasAlbums && hasRecordings) {
        return `${whiteboardMsg}${albumsMsg} ${recordingsMsg}`
    }
}

const generateExtraContentMessage = (model: User) => {
    const message: Message = {
        text: generateExtraContentText(model),
        system: true,
        extraContent: true,
    }

    return message
}

export const resolveClubShowMessage = (isClubShow: boolean, id: number) => {
    const { [id]: room } = getRoomState()

    if (!room) {
        return
    }

    removeExistingClubShowMessage(id)

    if (!isClubShow) {
        return
    }

    const message: Message = {
        clubShow: true,
        system: true,
        from: id,
    }

    setRoomMessagesState(message, id)
}

// private function
export const _setRoomOpenState = (isOpen: boolean, id: number) => {
    const { [id]: existingRoomState } = getRoomState()

    if (!existingRoomState) {
        return
    }

    setRoomState({ modifications: { [id]: { isOpen } } })
}

export const setRoomLoungeState = (isLounge: boolean, id: number) => {
    setRoomState({ modifications: { [id]: { isLounge } } })
}

export const setRoomLoungeSurrogateState = (
    loungeSurrogateID: number,
    id: number,
) => {
    const { [id]: existingRoomState } = getRoomState()

    if (!existingRoomState) {
        return
    }

    const { loungeSurrogateID: previousLoungeSurrogateID } = existingRoomState

    if (previousLoungeSurrogateID === loungeSurrogateID) {
        //
        // This condition happens only when navigating to a lounge with a
        // model already specified.
        //
        return
    }

    setRoomState({
        modifications: {
            [id]: {
                previousLoungeSurrogateID,
                loungeSurrogateID,
                isLounge: true,
            },
        },
    })
}

export const setRoomIDState = (id: number) => {
    // console.log(`[app-state: room ${id}] SETTING ROOM ID`)

    const { [id]: user } = USERSSTATE

    if (!user) {
        return
    }

    const { streamType } = user

    if (!streamType) {
        streamPublicShow(id)
    }

    setRoomState({
        modifications: {
            [id]: {
                id,
            },
        },
        immediately: true,
    })
}

export const setRoomMessagesState = async (message: Message, id: number) => {
    const { [id]: existingRoom } = getRoomState()

    if (!existingRoom) {
        return
    }

    const { isLounge } = existingRoom

    //
    // Marc: If messages are stale due to socket being disconnected, then clear
    // them. They were only a placeholder until the new ones came in.
    //

    const { topic: topicUpdate, welcome } = message
    const [processedMessage, processedPreviews] = await processLoungeMessage(
        message,
        isLounge,
    )
    // get the latest room state after the async operation (to avoid race conditions)
    const {
        messages: existingMessages = [],
        areMessagesStale,
        // ensure loungePreviews are set to an empty array if we're in a lounge
        loungePreviews: existingPreviews = [],
    } = getRoomState()[id]

    if (areMessagesStale) {
        setRoomMessagesStaleState(false, id)
    }

    const messages = [
        ...removeExistingExtraContent(
            areMessagesStale ? [] : existingMessages,
            { topicUpdate },
        ),
        processedMessage,
    ]
    const model = USERSSTATE[id]
    const { isClubShow, streamType, topic } = model
    const hasTopic = !!topic

    if ((welcome && !hasTopic) || topicUpdate) {
        messages.push(generateExtraContentMessage(USERSSTATE[id]))
    }

    if (welcome || topicUpdate) {
        //
        //
        // Marc: This individual reordering thing has gotten kind of silly. The
        // correct thing to do would likely be to make a function that looks for
        // "system: true" and reorder all messages at once.
        //
        //
        const reorederedEndShowMessages = reorderEndMessage(messages)

        const reorederedVoyeurMessages = reorderMessage(
            reorederedEndShowMessages || messages,
            (message) => message.voyeur,
        )

        const reorederedGrupshowMessages = reorderMessage(
            reorederedVoyeurMessages || reorederedEndShowMessages || messages,
            (message) => message.groupShow,
        )

        //
        // Marc: lol... What a chain.
        //
        const reorederedClubshowMessages = reorderMessage(
            reorederedGrupshowMessages ||
                reorederedVoyeurMessages ||
                reorederedEndShowMessages ||
                messages,
            (message) => message.clubShow,
            true,
        )

        const reorederedMessages =
            reorederedClubshowMessages ||
            reorederedGrupshowMessages ||
            reorederedVoyeurMessages ||
            reorederedEndShowMessages

        if (reorederedMessages) {
            // clearing out existing messages
            messages.length = 0
            messages.push(...reorederedMessages)
        }
    }

    const trimmedMessages =
        messages.length > 150 ? messages.slice(50) : messages

    if (processedPreviews.length > 0) {
        // eslint-disable-next-line fp/no-let
        let hasNew = false
        // eslint-disable-next-line fp/no-let
        let trimmedPreviews = existingPreviews.slice(0, 24)
        processedPreviews.forEach((processed) => {
            if (
                !trimmedPreviews.find(
                    (existingPreview) => existingPreview.id === processed.id,
                )
            ) {
                hasNew = true
                trimmedPreviews = [
                    {
                        id: processed.id,
                        slug: processed.slug,
                        sender: message.from,
                        timestamp: message.timestamp,
                        message: processedMessage.text
                            .replaceAll(/\{\{([^]+)\}\}/g, '')
                            .replaceAll(/:\d+:/g, '')
                            .trim(),
                    },
                    ...trimmedPreviews,
                ]
            }
        })

        setRoomState({
            modifications: {
                [id]: hasNew
                    ? {
                          messages: trimmedMessages,
                          loungePreviews: trimmedPreviews,
                      }
                    : { messages: trimmedMessages },
            },
        })
    } else {
        setRoomState({
            modifications: {
                [id]: isLounge
                    ? {
                          messages: trimmedMessages,
                          loungePreviews: existingPreviews,
                      }
                    : {
                          messages: trimmedMessages,
                      },
            },
        })
    }

    if (welcome && streamType === 'public') {
        resolveClubShowMessage(isClubShow, id)
    }
}

export const setRoomMessagesStaleState = (stale, id) => {
    setRoomState({
        modifications: {
            [id]: {
                areMessagesStale: stale,
            },
        },
        immediately: true,
    })
}

export const clearRoomMessagesState = (id: number) => {
    const { [id]: existingRoomState } = getRoomState()

    if (!existingRoomState) {
        return
    }

    setRoomState({
        modifications: {
            [id]: {
                messages: [],
            },
        },
    })
}

export const setRoomTipState = (tipMessage: TipMessage, id: number) => {
    const { [id]: existingRoom } = getRoomState()
    if (!existingRoom) {
        return
    }
    const { tips = [] } = existingRoom
    tips.push(tipMessage)
    setRoomState({
        modifications: {
            [id]: {
                tips: [...tips],
            },
        },
    })
}

export const clearRoomTipState = (id: number) => {
    const { [id]: existingRoom } = getRoomState()
    if (!existingRoom) {
        return
    }
    const { tips = [] } = existingRoom
    const clearedTips = tips.map((tip) => {
        tip.displayed = true
        return tip
    })
    setRoomState({
        modifications: {
            [id]: {
                tips: clearedTips,
            },
        },
    })
}

export const destroyRoomState = (id: number) => {
    // console.log(`[app-state: room ${id}] DESTROYING ROOM`)

    const { [id]: existingRoomState } = getRoomState()

    if (!existingRoomState) {
        return
    }

    setRoomState({ removals: { [id]: null }, immediately: true })

    unsubscribeRoom(id)
}

export const setRoomParticipantsState = (
    participants: Array<Participant>,
    id: number,
) => {
    setRoomState({
        modifications: {
            [id]: {
                participants: participants,
            },
        },
        immediately: true,
    })
}

export const setRoomInsertParticipantState = (
    participant: Participant,
    id: number,
) => {
    const { [id]: room } = getRoomState()

    if (!room) {
        return
    }

    // console.log('[app-state: participants] INSERT:', participantID)

    const { participants: existingParticipants = [] } = room

    const updatedParticipants =
        participant.id === id
            ? [participant, ...existingParticipants]
            : [...existingParticipants, participant]

    if (
        process.env.NODE_ENV === 'development' &&
        Array.from(new Set(updatedParticipants)).length !==
            updatedParticipants.length
    ) {
        console.error(
            '[app-state: participants] Found duplicate participants. This is not supposed to happen. Please check.',
            JSON.stringify(
                { existingParticipants, updatedParticipants, participant },
                null,
                4,
            ),
        )
    }

    setRoomState({
        modifications: {
            [id]: {
                participants: updatedParticipants,
            },
        },
    })
}

export const setRoomRemoveParticipantState = (
    participantID: number,
    id: number,
) => {
    const { [id]: room } = getRoomState()

    if (!room) {
        return
    }

    // console.log('[app-state: participants] REMOVE:', participantID)

    const { participants: existingParticipants = [] } = room

    const updatedParticipants = existingParticipants.filter(
        ({ id }) => participantID !== id,
    )

    if (
        process.env.NODE_ENV === 'development' &&
        existingParticipants.length === updatedParticipants.length
    ) {
        console.error(
            '[app-state: participants] Some how a participant was not removed. This is not supposed to happen. Please check.',
            JSON.stringify(
                { existingParticipants, updatedParticipants, participantID },
                null,
                4,
            ),
        )
    }

    setRoomState({
        modifications: {
            [id]: {
                participants: updatedParticipants,
            },
        },
    })
}

export const setRoomGuestCount = (guestCount: number, id: number) => {
    setRoomState({
        modifications: {
            [id]: {
                guestCount,
            },
        },
    })
}

export const setRoomNewsMessage = (message: Message) => {
    const rooms = getRoomState()

    Object.values(rooms).forEach(({ isOpen, id }) => {
        if (isOpen) {
            setRoomMessagesState({ ...message, system: true }, id)
        }
    })
}
